import { ThemeProvider } from '@emotion/react';

import * as React from 'react';

import { compose } from 'recompose';

import Retracked from 'js/app/retracked';
import { useLocation } from 'js/lib/useRouter';

import SearchPageHeader from 'bundles/composable-page-header-consumer/header-renderers/SearchPageHeader';
import UniversalPageFeatures from 'bundles/composable-page-header/components/UniversalPageFeatures';
import { useCourseraPlusProductOwnerships } from 'bundles/coursera-plus-common/utils/withCourseraPlusProductOwnerships';
import PageFooter from 'bundles/page-footer/components/PageFooter';
import withFetchLazyImages from 'bundles/page/components/shared/utils/withFetchLazyImages';
import { PageHeaderFooterProvider } from 'bundles/search-common/components/experiments/coachInSearch/PageHeaderFooterContext';
import { SearchExplainProvider } from 'bundles/search-common/components/internal-search-explainer/SearchExplainContext';
import useSearchExplainMetrics from 'bundles/search-common/components/internal-search-explainer/hooks/useSearchExplainMetrics';
import useSearchExplainState from 'bundles/search-common/components/internal-search-explainer/hooks/useSearchExplainState';
import { LearnerProvider } from 'bundles/search-common/providers/LearnerProvider';
import RouterProvider from 'bundles/search-common/providers/RouterProvider';
import SearchProvider from 'bundles/search-common/providers/SearchProvider';
import { searchTheme } from 'bundles/search-common/searchTheme';
import { inferQueryFromParams } from 'bundles/search-common/utils/eventingUtils';
import { getAutoApplyFilters } from 'bundles/search-common/utils/experimentUtils';
import { useSearchConfigs } from 'bundles/search-v2/hooks/useSearchConfigs';
import inServerContext from 'bundles/ssr/util/inServerContext';
import { CoachStateProvider } from 'bundles/unified-common/providers/CoachStateProvider';
import withMarketingConsent from 'bundles/user-consent/components/withMarketingConsent';

import 'css!bundles/search-common/__styles__/index';

type Props = {
  children: React.ReactNode;
};

const SearchApp = ({ children }: Props) => {
  const headerRef = React.useRef<HTMLDivElement>(null);
  const footerRef = React.useRef<HTMLDivElement>(null);
  const { ownsCourseraPlus, ownsCourseraLite } = useCourseraPlusProductOwnerships();
  const searchConfigs = useSearchConfigs(true);
  const shouldDoAutoFiltersLogic = getAutoApplyFilters();

  // Search explain state and metrics
  const { hasPermission, enableSearchExplain, toggleSearchExplain } = useSearchExplainState();
  const location = useLocation();
  const query = inferQueryFromParams(location?.query?.query) || '';
  const {
    metrics: queryMetrics,
    loading: metricsLoading,
    error: metricsError,
  } = useSearchExplainMetrics(query, enableSearchExplain);
  const searchExplainContextValue = React.useMemo(
    () => ({
      hasPermission,
      enableSearchExplain,
      toggleSearchExplain,
      queryMetrics,
      metricsLoading,
      metricsError: metricsError || undefined,
    }),
    [hasPermission, enableSearchExplain, toggleSearchExplain, queryMetrics, metricsLoading, metricsError]
  );

  // used to prevent browser from scrolling to the last scroll position when navigating back/refreshing.
  // needed due to infinite scroll implementation not handling scroll restoration. fixable with virtual scroll.
  if (!inServerContext && 'scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }

  return (
    <div className="rc-SearchApp">
      <UniversalPageFeatures />
      <SearchPageHeader />
      <ThemeProvider theme={searchTheme}>
        <RouterProvider>
          <SearchProvider searchConfigs={searchConfigs} shouldDoAutoFiltersLogic={shouldDoAutoFiltersLogic}>
            <SearchExplainProvider value={searchExplainContextValue}>
              <LearnerProvider ownsCourseraPlus={ownsCourseraPlus} ownsCourseraLite={ownsCourseraLite}>
                <PageHeaderFooterProvider headerRef={headerRef} footerRef={footerRef}>
                  <CoachStateProvider>{children}</CoachStateProvider>
                </PageHeaderFooterProvider>
              </LearnerProvider>
            </SearchExplainProvider>
          </SearchProvider>
        </RouterProvider>
      </ThemeProvider>
      <PageFooter elRef={footerRef} />
    </div>
  );
};

export default compose<Props, {}>(
  Retracked.createTrackedContainer(() => ({
    namespace: {
      app: 'search',
      page: 'search',
    },
  })),
  withFetchLazyImages,
  withMarketingConsent
)(SearchApp);
